import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
  Mail, LifeBuoy, ShieldCheck, Scale, Send, MessageSquare,
  Clock, AlertTriangle,
} from 'lucide-react';

import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { safeJsonLd } from '../utils/seo';
import '../styles/pages/ContactPage.css';

// Real LifeUp channels — match the addresses used across the legal pages.
const CHANNELS = [
  {
    icon: <LifeBuoy size={22} />,
    title: 'Suporte geral',
    email: 'suporte@lifyup.com.br',
    note: 'Dúvidas, sugestões e problemas com o app.',
  },
  {
    icon: <ShieldCheck size={22} />,
    title: 'Privacidade e LGPD',
    email: 'dpo@lifyup.com.br',
    note: 'Solicitações sobre seus dados pessoais.',
  },
  {
    icon: <Scale size={22} />,
    title: 'Jurídico e imprensa',
    email: 'legal@lifyup.com.br',
    note: 'Parcerias, imprensa e notificações legais.',
  },
];

const FAQ = [
  {
    q: 'O app é realmente gratuito?',
    a: 'Sim. O plano Free dá acesso a qualquer programa, contador, diário e comunidade base, para sempre.',
  },
  {
    q: 'Como faço para deletar minha conta?',
    a: 'No app, vá em Configurações → Conta → Excluir conta. Pelo site, acesse lifyup.com.br/excluir-conta.',
  },
  {
    q: 'Como reporto um bug?',
    a: 'Use o formulário acima selecionando "Reportar problema" e descreva o passo a passo para reproduzir.',
  },
  {
    q: 'Vocês oferecem terapia ou diagnóstico?',
    a: 'Não. A LifeUp é uma ferramenta de suporte ao bem-estar — não substitui acompanhamento profissional.',
  },
];

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      toast.success('Mensagem enviada! Responderemos em breve.');
      reset();
    } catch {
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contato — LifeUp</title>
        <meta
          name="description"
          content="Fale com a equipe LifeUp. Suporte geral, privacidade (LGPD) e jurídico — atendimento por e-mail em até 24h úteis."
        />
        <link rel="canonical" href="https://lifyup.com.br/contato" />
        <meta property="og:title" content="Contato — LifeUp" />
        <meta property="og:description" content="Suporte LifeUp por e-mail — geral, LGPD e jurídico." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lifyup.com.br/contato" />
        <script type="application/ld+json">{safeJsonLd({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contato — LifeUp',
          url: 'https://lifyup.com.br/contato',
          mainEntity: {
            '@type': 'Organization',
            name: 'LifeUp',
            url: 'https://lifyup.com.br',
            contactPoint: CHANNELS.map((c) => ({
              '@type': 'ContactPoint',
              contactType: c.title,
              email: c.email,
              areaServed: 'BR',
              availableLanguage: ['Portuguese'],
            })),
          },
        })}</script>
      </Helmet>

      <Toaster position="top-center" />

      <div className="lp-theme contact-page" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
        <Header />

        <main>
          {/* ===== Hero ===== */}
          <section className="contact-hero">
            <div className="container">
              <motion.div
                className="contact-hero__inner"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                <span className="contact-eyebrow">
                  <span className="contact-eyebrow__dot" />
                  Fale com a LifeUp
                </span>
                <h1 className="contact-hero__title">Como podemos te ajudar?</h1>
                <p className="contact-hero__subtitle">
                  Atendimento por e-mail em até <strong>24 horas úteis</strong>. Para emergências
                  emocionais, consulte os canais públicos no fim desta página.
                </p>

                <div className="contact-hero__badges" role="group" aria-label="Garantias de atendimento">
                  <span className="contact-hero__badge">
                    <Clock size={16} />
                    Resposta em 24h úteis
                  </span>
                  <span className="contact-hero__badge">
                    <ShieldCheck size={16} />
                    Confidencial
                  </span>
                  <span className="contact-hero__badge">
                    <MessageSquare size={16} />
                    Sem chatbot
                  </span>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ===== Form + channels ===== */}
          <section className="contact-main">
            <div className="container">
              <div className="contact-main__grid">
                {/* Form */}
                <motion.div
                  className="contact-form-card"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="contact-form-card__title">Envie uma mensagem</h2>
                  <p className="contact-form-card__hint">
                    Campos marcados com <span aria-hidden="true">*</span> são obrigatórios.
                  </p>

                  <form onSubmit={handleSubmit(onSubmit)} className="contact-form" noValidate>
                    <div className="contact-form__row">
                      <div className="contact-field">
                        <label htmlFor="name">Nome completo *</label>
                        <input
                          type="text"
                          id="name"
                          autoComplete="name"
                          aria-invalid={!!errors.name}
                          {...register('name', {
                            required: 'Nome é obrigatório',
                            minLength: { value: 3, message: 'Mínimo de 3 caracteres' },
                          })}
                          className={errors.name ? 'is-error' : ''}
                        />
                        {errors.name && <span className="contact-field__error">{errors.name.message}</span>}
                      </div>

                      <div className="contact-field">
                        <label htmlFor="email">E-mail *</label>
                        <input
                          type="email"
                          id="email"
                          autoComplete="email"
                          aria-invalid={!!errors.email}
                          {...register('email', {
                            required: 'E-mail é obrigatório',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'E-mail inválido',
                            },
                          })}
                          className={errors.email ? 'is-error' : ''}
                        />
                        {errors.email && <span className="contact-field__error">{errors.email.message}</span>}
                      </div>
                    </div>

                    <div className="contact-field">
                      <label htmlFor="subject">Assunto *</label>
                      <select
                        id="subject"
                        aria-invalid={!!errors.subject}
                        defaultValue=""
                        {...register('subject', { required: 'Selecione um assunto' })}
                        className={errors.subject ? 'is-error' : ''}
                      >
                        <option value="" disabled>Selecione...</option>
                        <option value="duvida">Dúvida sobre o app</option>
                        <option value="sugestao">Sugestão</option>
                        <option value="problema">Reportar problema</option>
                        <option value="parcerias">Parcerias</option>
                        <option value="imprensa">Imprensa</option>
                        <option value="lgpd">Privacidade / LGPD</option>
                        <option value="outro">Outro</option>
                      </select>
                      {errors.subject && <span className="contact-field__error">{errors.subject.message}</span>}
                    </div>

                    <div className="contact-field">
                      <label htmlFor="message">Mensagem *</label>
                      <textarea
                        id="message"
                        rows="6"
                        aria-invalid={!!errors.message}
                        placeholder="Conte com calma o que está acontecendo. Quanto mais detalhes, melhor podemos ajudar."
                        {...register('message', {
                          required: 'Mensagem é obrigatória',
                          minLength: { value: 10, message: 'Mínimo de 10 caracteres' },
                        })}
                        className={errors.message ? 'is-error' : ''}
                      />
                      {errors.message && <span className="contact-field__error">{errors.message.message}</span>}
                    </div>

                    <button
                      type="submit"
                      className="contact-submit"
                      disabled={isSubmitting}
                    >
                      <Send size={18} />
                      <span>{isSubmitting ? 'Enviando...' : 'Enviar mensagem'}</span>
                    </button>
                  </form>
                </motion.div>

                {/* Channels */}
                <aside className="contact-aside" aria-label="Canais de contato">
                  <h2 className="contact-aside__title">Canais diretos</h2>
                  <p className="contact-aside__hint">
                    Prefere e-mail? Escreva direto pelo canal específico.
                  </p>

                  <div className="contact-channels">
                    {CHANNELS.map((c, i) => (
                      <motion.a
                        key={c.email}
                        href={`mailto:${c.email}`}
                        className="contact-channel"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.08 }}
                      >
                        <div className="contact-channel__icon">{c.icon}</div>
                        <div className="contact-channel__body">
                          <h3>{c.title}</h3>
                          <span className="contact-channel__email">{c.email}</span>
                          <p>{c.note}</p>
                        </div>
                      </motion.a>
                    ))}
                  </div>

                  <div className="contact-inapp">
                    <div className="contact-inapp__icon" aria-hidden="true">
                      <MessageSquare size={22} />
                    </div>
                    <div>
                      <h3>Já usa o app?</h3>
                      <p>
                        Suporte e protocolo de emergência ficam disponíveis 24/7 dentro do
                        aplicativo, para quem já tem conta.
                      </p>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          {/* ===== FAQ ===== */}
          <section className="contact-faq" aria-labelledby="contact-faq-title">
            <div className="container">
              <header className="contact-section-header">
                <span className="contact-eyebrow">Perguntas frequentes</span>
                <h2 id="contact-faq-title" className="contact-section-header__title">
                  Antes de escrever, dá uma olhada aqui
                </h2>
                <p className="contact-section-header__subtitle">
                  Reunimos as perguntas que mais recebemos por e-mail. Se não resolver, mande
                  mensagem — respondemos pessoalmente.
                </p>
              </header>

              <div className="contact-faq__grid">
                {FAQ.map((item, i) => (
                  <motion.article
                    key={i}
                    className="contact-faq-item"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                  >
                    <h3>{item.q}</h3>
                    <p>{item.a}</p>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Emergency notice (LGPD / Apple 1.4.1 / Play Health) ===== */}
          <aside className="contact-emergency" aria-label="Em emergência">
            <div className="container">
              <div className="contact-emergency__icon" aria-hidden="true">
                <AlertTriangle size={22} />
              </div>
              <p>
                <strong>Em crise emocional?</strong> Se você está em sofrimento intenso ou
                tem pensamentos de autolesão, ligue para o <strong>CVV 188</strong> (24h, gratuito) ou{' '}
                <strong>SAMU 192</strong>. Fora do Brasil, contate o serviço local de emergência.
              </p>
            </div>
          </aside>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ContactPage;
